import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Context } from '../../store/Store'
import { planificationApi } from '../../services/planificationApi'

/**
 * Dashboard de planification
 * Affiche un tableau des planifications existantes du producteur
 */
export function PlanificationDashboard() {
  const { app } = React.useContext(Context)
  const [planifications, setPlanifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    // Attendre que le profil soit chargé avant d'appeler l'API
    if (!app.profil) return

    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = app.profil.code_ean || user.nicename?.toUpperCase()

    if (!producteurCode) {
      setLoading(false)
      return
    }

    setLoading(true)
    const fetchData = async () => {
      try {
        const records = await planificationApi.listByProducteur(producteurCode)
        if (!cancelled) {
          setPlanifications(records)
        }
      } catch (err) {
        if (!cancelled) {
          setError('Erreur lors du chargement des planifications')
          console.error(err)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [app.profil])

  if (loading) {
    return (
      <div className="m-6 text-center">
        <p>Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="m-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="m-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Mes Planifications</h2>
        <Link
          to="new"
          className="bg-base text-white px-4 py-2 rounded-md hover:bg-base-dark"
        >
          + Ajouter une ligne
        </Link>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Ce tableau affiche la quantit&eacute; totale planifi&eacute;e par culture. Cliquez sur &laquo; Modifier &raquo; pour ajuster les quantit&eacute;s mois par mois.
      </p>

      {planifications.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          Aucune planification pour le moment.
        </div>
      ) : (
        <>
        {/* Mobile: cartes */}
        <div className="space-y-3 md:hidden">
          {planifications.map((planif, index) => {
            const entries = Object.entries(planif.quantites || {})
            const totalQty = entries.reduce(
              (sum, [, q]) => sum + (typeof q === 'object' ? q.quantite : q),
              0
            )
            const monthCount = entries.length
            const legumeLabel = planif.legumeId.replace('|', ' ')

            return (
              <div key={planif.legumeId || index} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="font-medium text-base mb-2">{legumeLabel}</div>
                <div className="grid grid-cols-2 gap-1 text-sm text-gray-600 mb-3">
                  <div>Surface : {planif.surface ?? '-'} Ha</div>
                  <div>Certificat : {planif.certificat || '-'}</div>
                  <div>Total : <span className="font-semibold text-gray-900">{totalQty.toLocaleString('fr-FR')}</span> {planif.unite || 'KG'}</div>
                  <div>{monthCount} mois</div>
                </div>
                <div className="flex space-x-4">
                  <Link
                    to={`edit/${encodeURIComponent(planif.legumeId)}`}
                    className="text-sm text-base hover:text-base-dark font-medium"
                  >
                    Modifier
                  </Link>
                  <button
                    onClick={() => handleDelete(planif.legumeId)}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Desktop: tableau */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  L&eacute;gume
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Surface (Ha)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificat
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantit&eacute; totale
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mois d&eacute;clar&eacute;s
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {planifications.map((planif, index) => {
                const entries = Object.entries(planif.quantites || {})
                const totalQty = entries.reduce(
                  (sum, [, q]) => sum + (typeof q === 'object' ? q.quantite : q),
                  0
                )
                const monthCount = entries.length
                const legumeLabel = planif.legumeId.replace('|', ' ')

                return (
                  <tr key={planif.legumeId || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="font-medium">{legumeLabel}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {planif.surface ?? '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {planif.certificat || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="font-semibold">{totalQty.toLocaleString('fr-FR')}</span>{' '}
                      <span className="text-gray-500 text-sm">{planif.unite || 'KG'}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600">
                        {monthCount} mois
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`edit/${encodeURIComponent(planif.legumeId)}`}
                        className="text-base hover:text-base-dark mr-3"
                      >
                        Modifier
                      </Link>
                      <button
                        onClick={() => handleDelete(planif.legumeId)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )

  async function handleDelete(legumeId) {
    if (!window.confirm('Voulez-vous vraiment supprimer cette planification ?')) {
      return
    }

    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = app.profil?.code_ean || user.nicename?.toUpperCase()
    try {
      await planificationApi.remove({ producteurId: producteurCode, legumeId })
      setPlanifications((prev) => prev.filter((p) => p.legumeId !== legumeId))
    } catch (err) {
      console.error('Erreur suppression', err)
      alert('Erreur lors de la suppression')
    }
  }
}
